import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import { createPopper } from '@popperjs/core';
import classnames from 'classnames';
import React__default, { useRef, useState, useEffect } from 'react';
import alertUserOfDeprecatedProps from '../utils/deprecation.js';

var _excluded = ["as", "className", "content", "children", "location", "tabIndex", "tooltip", "data-testid"];
var positionTooltip = function positionTooltip(arrowRef, tooltipRef, tooltipContentRef, placement) {
  tooltipRef.current && tooltipContentRef.current && arrowRef.current ? createPopper(tooltipRef.current, tooltipContentRef.current, {
    modifiers: [{
      name: 'arrow',
      options: {
        padding: function padding(_ref) {
          var reference = _ref.reference,
            placement = _ref.placement;
          if (placement === 'bottom-start' && reference.width <= 36 || placement === 'top-start' && reference.width <= 36) {
            return 18;
          }
        }
      }
    }, {
      name: 'offset',
      options: {
        offset: [0, 8]
      }
    }, {
      name: 'flip',
      //flips popper with allowed placements
      options: {
        allowedAutoPlacements: ['right', 'left', 'top', 'bottom', 'top-end', 'top-start', 'bottom-end', 'bottom-start'],
        rootBoundary: 'viewport'
      }
    }],
    placement: placement
  }) : null;
};
var newLocation = function newLocation(location) {
  switch (location) {
    case 'top-left':
      {
        return 'top-start';
      }
    case 'top-right':
      {
        return 'top-end';
      }
    case 'bottom-left':
      {
        return 'bottom-start';
      }
    case 'bottom-right':
      {
        return 'bottom-end';
      }
    default:
      return location;
  }
};
var Tooltip = function Tooltip(props) {
  var _props$as = props.as,
    As = _props$as === void 0 ? 'span' : _props$as,
    className = props.className,
    content = props.content,
    children = props.children,
    _props$location = props.location,
    location = _props$location === void 0 ? 'none' : _props$location,
    _props$tabIndex = props.tabIndex,
    tabIndex = _props$tabIndex === void 0 ? '-1' : _props$tabIndex,
    tooltip = props.tooltip,
    dataTestId = props['data-testid'],
    otherProps = _objectWithoutProperties(props, _excluded);
  alertUserOfDeprecatedProps('Tooltip', otherProps);
  var arrowRef = useRef(null);
  var tooltipRef = useRef(null);
  var tooltipContentRef = useRef(null);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    showTooltip = _useState2[0],
    setShowTooltip = _useState2[1];
  useEffect(function () {
    if (showTooltip) {
      var _tooltipContentRef$cu;
      // Dom attribute change reflows in the main thread, therefore should block for synchronous tooltip positioning
      tooltipContentRef === null || tooltipContentRef === void 0 ? void 0 : (_tooltipContentRef$cu = tooltipContentRef.current) === null || _tooltipContentRef$cu === void 0 ? void 0 : _tooltipContentRef$cu.setAttribute('data-show', '');
      positionTooltip(arrowRef, tooltipRef, tooltipContentRef, newLocation(location));
    } else {
      var _tooltipContentRef$cu2;
      tooltipContentRef === null || tooltipContentRef === void 0 ? void 0 : (_tooltipContentRef$cu2 = tooltipContentRef.current) === null || _tooltipContentRef$cu2 === void 0 ? void 0 : _tooltipContentRef$cu2.removeAttribute('data-show');
    }
  }, [showTooltip]);
  var show = function show() {
    setShowTooltip(true);
  };
  var hide = function hide() {
    setShowTooltip(false);
  };
  var finalClasses = classnames('C-Tooltip', className);
  var tooltipContent = tooltip || content;
  if (location === 'none') {
    return /*#__PURE__*/React__default.createElement(As, _extends({
      className: finalClasses,
      "data-tooltip": tooltipContent,
      "data-testid": dataTestId,
      tabIndex: tabIndex
    }, otherProps), /*#__PURE__*/React__default.createElement("span", {
      role: "tooltip",
      className: "hc-sr-only"
    }, tooltipContent), children);
  }
  return /*#__PURE__*/React__default.createElement("span", _extends({
    ref: tooltipRef,
    className: finalClasses,
    "aria-describedby": "tooltip",
    "data-testid": dataTestId,
    tabIndex: tabIndex,
    onMouseEnter: function onMouseEnter() {
      show();
    },
    onMouseLeave: function onMouseLeave() {
      hide();
    },
    onFocus: function onFocus() {
      show();
    },
    onBlur: function onBlur() {
      hide();
    },
    onPointerLeave: function onPointerLeave() {
      hide();
    },
    onPointerEnter: function onPointerEnter() {
      show();
    }
  }, otherProps), /*#__PURE__*/React__default.createElement("div", {
    role: "tooltip",
    className: "C-Tooltip__Content",
    "data-testid": dataTestId ? "".concat(dataTestId, "-tooltipContent") : null,
    ref: tooltipContentRef
  }, tooltipContent, /*#__PURE__*/React__default.createElement("div", {
    className: 'C-Tooltip__Arrow',
    "data-popper-arrow": true,
    ref: arrowRef
  })), children);
};
Tooltip.displayName = 'Tooltip';

export { Tooltip as default };
